import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { 
  capabilities, 
  whyChooseUs, 
  rfqProcess, 
  globalNetwork, 
  qualitySteps,
  caseStudies,
  blogPosts,
  faqs,
  companyLogos,
  heroImages 
} from '../data/mockData';
import { ArrowRight, CheckCircle2, Globe, Shield, Clock, TrendingDown, Users } from 'lucide-react';

export const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-20 sm:pt-24 md:pt-32 pb-12 sm:pb-16 md:pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={heroImages.main} 
            alt="Precision Manufacturing" 
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/80"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 sm:mb-6 leading-tight">
                Precision Parts Manufacturing for Canadian Product Teams
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-6 sm:mb-8 leading-relaxed">
                Your single point of accountability for CNC machining, injection molding, and assembly—backed by our qualified manufacturing network across Canada, Malaysia, China, Mexico, and Vietnam.
              </p>
              
              <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
                <div className="flex items-center space-x-2">
                  <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-accent flex-shrink-0" />
                  <span className="text-xs sm:text-sm">Prototype → Production</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-accent flex-shrink-0" />
                  <span className="text-xs sm:text-sm">Quality control oversight</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-accent flex-shrink-0" />
                  <span className="text-xs sm:text-sm">Global supply chain support</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-accent flex-shrink-0" />
                  <span className="text-xs sm:text-sm">Canadian project management</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link to="/contact" className="w-full sm:w-auto">
                  <Button size="lg" className="w-full sm:w-auto bg-accent hover:bg-accent/90 transition-smooth group">
                    Request a Quote
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/contact" className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto transition-smooth">
                    Book Discovery Call
                  </Button>
                </Link>
              </div>
            </div>

            <div className="hidden lg:block">
              <img 
                src="https://images.unsplash.com/photo-1713371398485-7bde1bde9def?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHw0fHxDTkMlMjBtYWNoaW5pbmclMjBwcmVjaXNpb24lMjBwYXJ0c3xlbnwwfHx8fDE3Nzg4Mjc1NTl8MA&ixlib=rb-4.1.0&q=85" 
                alt="CNC Precision Machining" 
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Snapshot */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-3 sm:mb-4">Manufacturing Capabilities</h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto px-4">
              Complete manufacturing capabilities for precision components in metals and plastics
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {capabilities.map((capability) => (
              <Card key={capability.id} className="group hover:shadow-lg transition-smooth overflow-hidden">
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={capability.image} 
                    alt={capability.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardContent className="p-6">
                  <Badge variant="outline" className="mb-3">{capability.category}</Badge>
                  <h3 className="text-xl font-bold mb-2 group-hover:text-accent transition-smooth">
                    {capability.title}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4">{capability.shortDesc}</p>
                  <Link to={`/capabilities/${capability.slug}`}>
                    <Button variant="ghost" size="sm" className="group/btn">
                      Learn More 
                      <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-4">
              <strong>Materials:</strong> Aluminum, steel, stainless steel, engineering plastics, zinc alloys
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-3 sm:mb-4">Built for Canadian Procurement Teams</h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {whyChooseUs.map((item, index) => (
              <Card key={index} className="hover:shadow-lg transition-smooth">
                <CardContent className="p-6">
                  <div className="h-12 w-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
                    {index === 0 && <Shield className="h-6 w-6 text-accent" />}
                    {index === 1 && <Clock className="h-6 w-6 text-accent" />}
                    {index === 2 && <TrendingDown className="h-6 w-6 text-accent" />}
                    {index === 3 && <Users className="h-6 w-6 text-accent" />}
                    {index === 4 && <CheckCircle2 className="h-6 w-6 text-accent" />}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <ul className="space-y-2">
                    {item.points.map((point, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm text-muted-foreground">
                        <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* RFQ Process */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-foreground mb-4">From Inquiry to Delivery</h2>
            <p className="text-lg text-muted-foreground">Our streamlined RFQ process</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {rfqProcess.map((step, index) => (
              <Card key={index} className="relative hover:shadow-lg transition-smooth">
                <CardContent className="p-6">
                  <div className="text-6xl font-bold text-accent/20 mb-4">{step.step}</div>
                  <h3 className="text-lg font-bold mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </CardContent>
                {index < rfqProcess.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ArrowRight className="h-6 w-6 text-accent" />
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Global Network */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-foreground mb-4">Strategic Locations, Unified Management</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Global manufacturing network spanning five countries providing capacity, cost optimization, and supply chain resilience
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-12">
            {globalNetwork.map((location, index) => (
              <Card key={index} className="hover:shadow-lg transition-smooth">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Globe className="h-8 w-8 text-accent" />
                    <h3 className="text-2xl font-bold">{location.country}</h3>
                  </div>
                  <p className="text-muted-foreground mb-4">{location.description}</p>
                  <ul className="space-y-2">
                    {location.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="bg-primary text-primary-foreground">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold mb-4">Benefits of Our Global Network</h3>
              <div className="grid md:grid-cols-4 gap-6">
                <div>
                  <h4 className="font-semibold mb-2">Capacity Across Time Zones</h4>
                  <p className="text-sm text-primary-foreground/80">24/7 production capability</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Regional Advantages</h4>
                  <p className="text-sm text-primary-foreground/80">Optimize costs through strategic sourcing</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Supply Chain Redundancy</h4>
                  <p className="text-sm text-primary-foreground/80">Risk mitigation and flexibility</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Consistent Quality</h4>
                  <p className="text-sm text-primary-foreground/80">Unified standards across locations</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Quality Control */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-foreground mb-6">Quality Control at Every Step</h2>
              <p className="text-lg text-muted-foreground mb-8">
                Rigorous quality processes ensure consistent, reliable parts that meet your specifications.
              </p>
              
              <div className="space-y-4">
                {qualitySteps.map((step, index) => (
                  <Card key={index} className="hover:shadow-md transition-smooth">
                    <CardContent className="p-4">
                      <h3 className="font-bold mb-1">{step.title}</h3>
                      <p className="text-sm text-muted-foreground">{step.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <p className="text-sm text-muted-foreground mt-6 italic">
                Certifications available upon request based on project requirements
              </p>
            </div>

            <div>
              <img 
                src={heroImages.qualityControl} 
                alt="Quality Control" 
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-foreground mb-4">Recent Project Examples</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => (
              <Card key={index} className="hover:shadow-lg transition-smooth">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-3">{study.title}</h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="font-semibold text-accent">Challenge:</span>
                      <p className="text-muted-foreground mt-1">{study.challenge}</p>
                    </div>
                    <div>
                      <span className="font-semibold text-accent">Process:</span>
                      <p className="text-muted-foreground mt-1">{study.process}</p>
                    </div>
                    <div>
                      <span className="font-semibold text-accent">Outcome:</span>
                      <p className="text-muted-foreground mt-1">{study.outcome}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Company Logos */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-center text-lg font-semibold text-muted-foreground mb-8">
            Your Parts Will Be Made in the Same Facility as Top Companies
          </h3>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-8 items-center opacity-60 grayscale hover:grayscale-0 transition-all duration-300">
            {companyLogos.slice(0, 6).map((company, index) => (
              <div key={index} className="flex items-center justify-center">
                <img 
                  src={company.logo} 
                  alt={company.name} 
                  className="h-12 object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-foreground mb-4">General FAQ's</h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border rounded-lg px-6">
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="font-semibold">{index + 1}. {faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Start Your Next Project?</h2>
          <p className="text-xl mb-8 text-primary-foreground/90">
            Get your RFQ response within 24-48 hours. Let's discuss your manufacturing needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground">
                Request a Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href="mailto:rfq@allpartsmanufacturing.com">
              <Button size="lg" variant="outline" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                Email RFQ: rfq@allpartsmanufacturing.com
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Latest Blog */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-4xl font-bold text-foreground mb-2">Latest Insights</h2>
              <p className="text-lg text-muted-foreground">Articles and guides with useful information</p>
            </div>
            <Link to="/blog">
              <Button variant="outline" className="group">
                Read More
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {blogPosts.slice(0, 3).map((post) => (
              <Card key={post.id} className="group hover:shadow-lg transition-smooth overflow-hidden">
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-2 mb-3">
                    <Badge variant="outline">{post.category}</Badge>
                    <span className="text-xs text-muted-foreground">{post.readTime}</span>
                  </div>
                  <h3 className="text-lg font-bold mb-2 group-hover:text-accent transition-smooth line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{post.excerpt}</p>
                  <Link to={`/blog/${post.slug}`}>
                    <Button variant="ghost" size="sm" className="group/btn p-0 h-auto">
                      Read Article
                      <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
